// components/exercise/ExerciseCategoriesDropdown.jsx
import React from "react";
import { useTranslation } from "react-i18next";
import ButtonLoader from "@components/ui/Loaders/ButtonLoader";
import ExercisePlanMultiSelectDropdown from "@components/ui/customDropdowns/ExercisePlanMultiSelectDropdown";
import { SaveAllIcon } from "lucide-react";
import BackImg from "@assets/images/stepsBack.jpg";

const ExerciseCategoriesDropdown = ({
  selectedTags,
  errors,
  localExercisePreferences,
  icons,
  getDropdownOptions,
  getDisplayLabel,
  handleMultiSelectChange,
  handleSelectChange,
  removeTag,
  subscription,
  generatePlanLoading,
  saveLoad,
  handleClickOpen,
  navigate,
  t
}) => {
  return (
    <div className="rounded-[12px] p-[24px] bg-white">
      <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-5">
        <h2 className="text-[14px] md:text-[16px] font-[Lato] font-[500] text-black">
          {t("exercisePlan.exerciseCategoriesHeading")}
        </h2>
        <div className="flex items-center gap-2">
          <button
            className={`bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] mt-2 md:m-0 text-white px-3 min-h-[30px] w-[100%] md:min-w-[182px]  
              ${generatePlanLoading && "opacity-70"}
              text-[12px] font-[500] font-[Lato] rounded-md`}
            onClick={() =>
              subscription?.status === "active" ||
              subscription?.status === "pending-cancel"
                ? handleClickOpen()
                : navigate("/renew-subscription")
            }
            disabled={generatePlanLoading || saveLoad}
          >
            {saveLoad ? (
              <ButtonLoader />
            ) : (
              <div className="flex items-center gap-1 justify-center md:justify-start">
                <SaveAllIcon className="w-5 h-5" />
                <span>{t("exercisePlan.confirmGenerateButton")}</span>
              </div>
            )}
          </button>
        </div>
      </div>

      <div
        className="bg-cover rounded-[12px] p-[24px] gap-4 grid gird-cols-1"
        style={{ backgroundImage: `url(${BackImg})` }}
      >
        {localExercisePreferences.map(({ key, type }) => {
          const dropdownOptions = getDropdownOptions(key);
          const selectedValue = selectedTags[key];
          const selectedArray = Array.isArray(selectedValue)
            ? selectedValue
            : selectedValue
            ? [selectedValue]
            : [];

          return (
            <div
              key={key}
              className="flex flex-col md:flex-row xs:items-center bg-white gap-2 rounded-[12px] p-3"
            >
              <div className="flex gap-2 items-center">
                <div
                  className="min-w-[54px] max-w-[54px] max-h-[54px] min-h-[54px] p-2 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: icons[key].bgColor }}
                >
                  {icons[key].svg}
                </div>

                <p className="block md:hidden text-black text-[14px] font-[Lato] mb-2">
                  {t(`firstFormSteps.${key}.label`)}
                </p>
              </div>
              <div className="flex flex-col flex-1">
                <p className="hidden md:block text-black text-[14px] font-[Lato] mb-2">
                  {t(`firstFormSteps.${key}.label`)}
                </p>

                <div className="relative w-full mr-5">
                  {type === "multi-select" && (
                    <ExercisePlanMultiSelectDropdown
                      options={dropdownOptions.map((opt) => opt.value)}
                      selectedValues={selectedTags[key] || []}
                      onSelect={(values) => handleMultiSelectChange(key, values)}
                      label={t(`firstFormSteps.${key}.label`)}
                      hasError={!!errors[key]}
                      placeholder="Select options"
                      isEditable={true}
                      isFistForm={true}
                      displayMap={dropdownOptions.reduce((map, opt) => {
                        map[opt.value] = opt.label;
                        return map;
                      }, {})}
                      fieldKey={key}
                    />
                  )}
                  {type === "select" && (
                    <>
                      <select
                        className={`border p-2 rounded-md w-[100%] h-10 text-[12px] outline-none bg-white ${
                          errors[key] ? "border-red-500" : ""
                        }`}
                        value={selectedValue || ""}
                        onChange={(e) => handleSelectChange(key, e.target.value)}
                      >
                        <option value="" disabled>
                          {t("preferences.validation.onlyOne")}
                        </option>
                        {dropdownOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      {errors[key] && (
                        <div className="text-red-500 text-[10px] md:text-[12px] ml-2 mt-1">
                          {t("validation.required", {
                            field: t(`firstFormSteps.${key}.label`),
                          })}
                        </div>
                      )}
                    </>
                  )}
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {selectedArray.map((itemValue) => (
                    <span
                      key={itemValue}
                      className="bg-blue-100 text-blue-600 px-2 py-1 rounded-md text-sm flex items-center"
                    >
                      {getDisplayLabel(key, itemValue)}
                      {type === "multi-select" && (
                        <button
                          onClick={() => removeTag(key, itemValue)}
                          className="ml-1 text-blue-400 hover:text-blue-700"
                        >
                          ✕
                        </button>
                      )}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ExerciseCategoriesDropdown;